import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLang, STR } from "@/i18n/translations";
import api from "@/lib/api";

const empty = { name: "", organization: "", email: "", phone: "", event_type: "", city: "", date: "", artist: "", budget: "", message: "" };

export default function Booking() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [artists, setArtists] = useState([]);

  useEffect(() => {
    api.get("/artists").then((r) => setArtists(r.data)).catch(() => {});
    const a = params.get("artist");
    if (a) setForm((f) => ({ ...f, artist: a }));
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/booking-requests", form);
      setDone(true);
      toast.success(t(STR.booking.success));
      setForm(empty);
    } catch {
      toast.error(t({ fr: "Erreur, vérifiez vos champs.", en: "Error, check your fields." }));
    } finally {
      setLoading(false);
    }
  };

  const F = STR.booking.fields;

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <p className="eyebrow mb-5">{t(STR.booking.eyebrow)}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold">{t(STR.booking.title)}</h1>
        <p className="text-cream/60 mt-5 text-lg">{t(STR.booking.subtitle)}</p>
        <div className="gold-line w-24 my-10" />

        {done ? (
          <div className="card-dark rounded-2xl p-12 text-center" data-testid="booking-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-accent mx-auto mb-5" />
            <p className="text-xl">{t(STR.booking.success)}</p>
            <button onClick={() => setDone(false)} className="btn-outline-gold rounded-full px-6 py-3 mt-8">
              {t({ fr: "Nouvelle demande", en: "New request" })}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="card-dark rounded-2xl p-8 lg:p-10 space-y-5" data-testid="booking-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={t(F.name)} required value={form.name} onChange={set("name")} testid="booking-name" />
              <Field label={t(F.organization)} value={form.organization} onChange={set("organization")} testid="booking-org" />
              <Field label={t(F.email)} type="email" required value={form.email} onChange={set("email")} testid="booking-email" />
              <Field label={t(F.phone)} value={form.phone} onChange={set("phone")} testid="booking-phone" />
              <Field label={t(F.eventType)} value={form.event_type} onChange={set("event_type")} testid="booking-event-type" />
              <Field label={t(F.city)} value={form.city} onChange={set("city")} testid="booking-city" />
              <Field label={t(F.date)} type="date" value={form.date} onChange={set("date")} testid="booking-date" />
              <div>
                <label className="text-xs uppercase tracking-widest text-cream/50 mb-2 block">{t(F.artist)}</label>
                <select value={form.artist} onChange={set("artist")} data-testid="booking-artist" className="w-full bg-white/5 border border-gold/20 rounded px-3 py-3 text-sm outline-none focus:border-gold">
                  <option value="">—</option>
                  {artists.map((a) => <option key={a.id} value={a.name}>{a.name}</option>)}
                </select>
              </div>
              <Field label={t(F.budget)} value={form.budget} onChange={set("budget")} testid="booking-budget" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-cream/50 mb-2 block">{t(F.message)}</label>
              <textarea rows={5} value={form.message} onChange={set("message")} data-testid="booking-message" className="w-full bg-white/5 border border-gold/20 rounded px-3 py-3 text-sm outline-none focus:border-gold resize-none" />
            </div>
            <button type="submit" disabled={loading} data-testid="booking-submit-btn" className="btn-gold rounded-full w-full py-4 flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {t(STR.common.requestBooking)}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-cream/50 mb-2 block">{label}{required && <span className="text-gold"> *</span>}</label>
      <input type={type} value={value} onChange={onChange} required={required} data-testid={testid}
             className="w-full bg-white/5 border border-gold/20 rounded px-3 py-3 text-sm outline-none focus:border-gold" />
    </div>
  );
}
